import React, { CSSProperties, useState } from 'react';
import {
  boxWidth,
  CaseSensitiveButton,
  ConditionType,
  Filter,
  flipFilter,
  getEmptyExpression,
  getExpressionType,
  getIngressExpression,
  getSourcesExp,
  OperatorType,
  RelationsProps,
} from '@fabric-ui/components';
import { emptyObject } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { cn } from '@utils/cn';
import { entityViewConfig } from '@utils/entityViewConfig';
import { SchemaTypes } from '../../views/Sources/Mapping/types';
import { FieldSelectorSection, FREE_SEARCH_FIELD, isFreeSearchField } from './components/FieldSelectorSection';
import { FilterRowActions } from './components/FilterRowActions';
import { OperatorSelectorSection } from './components/OperatorSelectorSection';
import { useFreeSearchAutoFocus } from './components/useFreeSearchAutoFocus';
import { ValueInputSection } from './components/ValueInputSection';
import { useExpressionBuilder } from './ExpressionBuilderContext';
import { isDateFilterField } from './FieldFilterBox.utils';
import { useFilterConfig, useFilterContext } from './FilterContext';
import { FreeSearchMatchMode } from './FreeSearchInput';
import UnifiedIngressToggle from './UnifiedIngressToggle';

export { FREE_SEARCH_FIELD };
export { FreeSearchMatchMode };

export interface FieldFilterBoxBaseProps {
  rootOperator: OperatorType | 'expression';
  filter: Filter;
  setFilter: (filter: Filter) => void;
  level?: number;
  onDelete: () => void;
  addLogicalExpression?: () => void;
  isIngressExp?: boolean;
  customSx?: CSSProperties;
  showDeleteAddFilter?: boolean;
  showDisableToggle?: boolean;
  initialEnable?: boolean;
}

export const FieldFilterBoxBase = ({
  rootOperator,
  filter,
  setFilter,
  level = 0,
  onDelete,
  addLogicalExpression,
  isIngressExp = false,
  customSx = {},
  showDeleteAddFilter = true,
  showDisableToggle = false,
  initialEnable = true,
}: FieldFilterBoxBaseProps) => {
  const {
    canSelectFieldType = true,
    defaultField = '',
    fieldOnly = false,
    hasListTab = false,
    ingressModeProps,
    relationsProps = emptyObject as RelationsProps,
    tokenSuggestions,
  } = useExpressionBuilder();
  const { activeProjName: contextActiveProjName, isVertical } = useFilterConfig();
  const { accountEntities } = useFilterContext();
  const [enableFilter, setEnableFilter] = useState(initialEnable);
  const fieldMap = accountEntities?.fieldMap ?? {};
  const activeProjName = contextActiveProjName ?? entityViewConfig.Asset.projectionName;
  const sourcesValue = filter.and?.operands[0].expression?.arrayCondition?.underlying.stringCondition?.contains || null;
  const ingressFilterExpression = filter.and?.operands[1];
  const isIngressMode = (ingressModeProps && !ingressModeProps.showToggle) || isIngressExp;
  const actualFilterValue = ingressFilterExpression || filter;

  const setIngressFilterExpression = (newExpression: Filter) => {
    const nextFieldName = newExpression?.expression?.fieldName;
    if (!nextFieldName) return;
    setFilter({
      ...filter,
      and: { ...filter.and, operands: [getSourcesExp(sourcesValue, nextFieldName.split('.')[0]), newExpression] },
    });
  };
  const setEffectiveFilter = isIngressMode ? setIngressFilterExpression : setFilter;
  const setCorrectFilter = (newFilter: Filter) =>
    isIngressMode ? setIngressFilterExpression(newFilter) : setFilter({ ...filter, ...newFilter });

  const typeCondition = (actualFilterValue.expression && getExpressionType(actualFilterValue.expression)) || ConditionType.string;
  const fieldName = actualFilterValue.freeText
    ? FREE_SEARCH_FIELD
    : (actualFilterValue.expression?.arrayCondition?.underlying || actualFilterValue.expression)?.fieldName ||
      actualFilterValue.and?.operands[0]?.expression?.fieldName ||
      actualFilterValue.or?.operands[0]?.expression?.fieldName;
  const isRepeated = canSelectFieldType ? !!actualFilterValue.expression?.arrayCondition : fieldMap[fieldName!]?.repeated;
  const conditionType = Object.keys(ConditionType).find(type => ConditionType[type] === typeCondition) || SchemaTypes.STRING;
  const typeName = `${conditionType}${isRepeated ? '[]' : ''}`;
  const innerExpression = actualFilterValue.expression?.arrayCondition?.underlying || actualFilterValue.expression;
  const currentConditionValue =
    innerExpression?.[typeCondition] && Object.values(innerExpression[typeCondition]).find(value => typeof value === 'string');
  const isOperatorLockedByToken = !!tokenSuggestions?.some(token => token.value === currentConditionValue);
  const showListTab =
    !!fieldName && (typeCondition === ConditionType.population || typeCondition === ConditionType.string) && !isRepeated && hasListTab;
  const isEmptyDate = !!actualFilterValue.expression?.stringCondition?.empty;
  const isNotEmptyDate = !!actualFilterValue.expression?.stringCondition?.notEmpty;
  const shouldRenderOperator = !(isDateFilterField(fieldName, fieldMap) && (isEmptyDate || isNotEmptyDate));
  const { markFreeSearchSelected, shouldAutoFocusFreeText } = useFreeSearchAutoFocus(fieldName);

  const onChangeEnableFilter = (enabled: boolean) => {
    setEnableFilter(enabled);
    setFilter(getEmptyExpression(enabled ? defaultField : undefined));
  };
  const onSelectSource = (source: string[] | string) => {
    const newSourcesExpression = getSourcesExp(ingressModeProps?.multiSelect ? (source as string[]).join() : source);
    setFilter({ ...filter, and: { ...filter.and, operands: [newSourcesExpression, ingressFilterExpression!] } });
  };

  return (
    <Box
      className={cn(
        '[&:hover_.option]:opacity-100 [&_.option]:transition-opacity [&_.option]:duration-150',
        isVertical && 'flex items-center gap-1',
        !enableFilter && showDisableToggle && 'bg-surface-interactive-primary-disabled'
      )}>
      <Box className="rounded-r border border-border-base-primary" style={{ width: isVertical ? boxWidth : 850, ...customSx }}>
        <FilterRowActions
          placement="header"
          rootOperator={rootOperator}
          level={level}
          onDelete={onDelete}
          addLogicalExpression={addLogicalExpression}
          enableFilter={enableFilter}
          onEnableChange={onChangeEnableFilter}
          showDeleteAddFilter={showDeleteAddFilter}
          showDisableToggle={showDisableToggle}
        />
        <Box className={cn('flex gap-1', isVertical ? 'flex-col items-start p-[10px]' : 'items-center justify-start p-3')}>
          {ingressModeProps && (
            <UnifiedIngressToggle
              onChangeIngressExp={() =>
                setFilter(
                  isIngressMode
                    ? flipFilter({ filter: actualFilterValue, isUberToIngress: false })
                    : getIngressExpression(flipFilter({ filter: actualFilterValue, isUberToIngress: true }))
                )
              }
              sourcesValue={sourcesValue?.split(',') || []}
              isIngressExp={isIngressMode}
              onChangeSources={onSelectSource}
              activeProjName={activeProjName}
              showToggle={ingressModeProps.showToggle}
              multiSelect={ingressModeProps.multiSelect}
              isIconCollapsed={ingressModeProps.isIconCollapsed}
            />
          )}
          <FieldSelectorSection
            enableFilter={enableFilter}
            fieldName={fieldName}
            filter={filter}
            isIngressMode={isIngressMode}
            isRepeated={isRepeated}
            onFreeSearchSelected={markFreeSearchSelected}
            setCorrectFilter={setCorrectFilter}
            setFilter={setFilter}
            typeCondition={typeCondition}
          />
          {!fieldOnly &&
            (isFreeSearchField(fieldName) ? (
              <ValueInputSection
                actualFilterValue={actualFilterValue}
                autoFocusFreeSearch={shouldAutoFocusFreeText}
                enableFilter={enableFilter}
                fieldName={fieldName}
                filter={filter}
                isOperatorLockedByToken={isOperatorLockedByToken}
                setEffectiveFilter={setEffectiveFilter}
                setFilter={setFilter}
                showListTab={showListTab}
                typeCondition={typeCondition}
                typeName={typeName}
              />
            ) : (
              <>
                {shouldRenderOperator && (
                  <OperatorSelectorSection
                    actualFilterValue={actualFilterValue}
                    enableFilter={enableFilter}
                    fieldName={fieldName}
                    filter={filter}
                    isOperatorLockedByToken={isOperatorLockedByToken}
                    isRepeated={isRepeated}
                    setCorrectFilter={setCorrectFilter}
                    showListTab={showListTab}
                    typeCondition={typeCondition}
                    typeName={typeName}
                  />
                )}
                <ValueInputSection
                  actualFilterValue={actualFilterValue}
                  autoFocusFreeSearch={false}
                  enableFilter={enableFilter}
                  fieldName={fieldName}
                  filter={filter}
                  isOperatorLockedByToken={isOperatorLockedByToken}
                  setEffectiveFilter={setEffectiveFilter}
                  setFilter={setFilter}
                  showListTab={showListTab}
                  typeCondition={typeCondition}
                  typeName={typeName}
                />
              </>
            ))}
          {relationsProps.enableCaseSensitive && filter.expression?.fieldCondition?.equals && (
            <CaseSensitiveButton actualFilterValue={actualFilterValue} filter={filter} setFilter={setFilter} />
          )}
          {!isVertical && (
            <FilterRowActions
              placement="row"
              rootOperator={rootOperator}
              level={level}
              onDelete={onDelete}
              addLogicalExpression={addLogicalExpression}
              enableFilter={enableFilter}
              onEnableChange={onChangeEnableFilter}
              showDeleteAddFilter={showDeleteAddFilter}
              showDisableToggle={showDisableToggle}
            />
          )}
        </Box>
      </Box>
      {isVertical && (
        <FilterRowActions
          placement="row"
          rootOperator={rootOperator}
          level={level}
          onDelete={onDelete}
          addLogicalExpression={addLogicalExpression}
          enableFilter={enableFilter}
          onEnableChange={onChangeEnableFilter}
          showDeleteAddFilter={showDeleteAddFilter}
          showDisableToggle={showDisableToggle}
        />
      )}
    </Box>
  );
};
